import React from "react";

type RefundType = "ticket" | "package" | "shopping" | "options" | "deposit";
type RefundTone = "safe" | "warning" | "danger";

interface RefundPolicySectionProps {
  type: RefundType;
  cancelRuleAgree: boolean;
  purchaseOneAgree: boolean;
  purchaseAllAgree?: boolean;
  onCancelRuleChange: (v: boolean) => void;
  onPurchaseAgreeChange: (v: boolean) => void;
  onAllAgreeChange?: (v: boolean) => void;
}

function RefundText({ type }: { type: RefundType }) {
  if (type === "package") {
    const refundRows: { condition: string; value: string; tone: RefundTone }[] = [
      { condition: "결제 후 1시간 이내", value: "100% 환불", tone: "safe" },
      { condition: "예약 3일 전까지", value: "100% 환불", tone: "safe" },
      { condition: "예약 2일 전", value: "30% 차감", tone: "warning" },
      { condition: "예약 1일 전", value: "50% 차감", tone: "warning" },
      { condition: "예약 당일", value: "환불 불가", tone: "danger" },
    ];
    const toneClassName: Record<RefundTone, string> = {
      safe: "text-emerald-600",
      warning: "text-[#e16036]",
      danger: "text-red-700 font-black",
    };

    return (
      <>
        <div className="mb-3 overflow-hidden  border border-gray-100 bg-orange-100/30">
          {refundRows.map((row) => (
            <div
              key={row.condition}
              className="flex items-center justify-between gap-4 px-4 py-3 last:border-b-0"
            >
              <span className="text-sm font-semibold text-gray-900">{row.condition}</span>
              <span className={`shrink-0 text-sm font-bold text ${toneClassName[row.tone]}`}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
        <p className="flex flex-wrap my-8 text-sm text-gray-900">
          결제 후 1시간 이내 취소 규칙이 이용일 기준 규정보다 우선으로 적용됩니다.
        </p>
      </>
    );
  }

  if (type === "shopping") {
    return (
      <div className="text-muted mb-3">
        <span>배송 관련 질문은 문의게시판 혹은 카카오톡 채널로 문의주시기 바랍니다.</span>
        <br />
        <span>불량 및 교환 문의는 반드시 고객센터를 통해서 문의주세요.</span>
      </div>
    );
  }

  if (type === "deposit") {
    return (
      <p className="text-muted mb-3">
        청소보증금은 체크아웃 요청이 관리자에 의해 승인 되면 자동 환불
      </p>
    );
  }

  return (
    <p className="mb-3 text-[#555] leading-relaxed">
      사용기간 내의 미사용 100% 환불
      <br />
      (유효기간 이후 환불불가)
    </p>
  );
}

function AgreementCheckbox({ checked, onChange, children, bold }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <label className={`flex items-center gap-2.5 cursor-pointer ${bold ? "font-semibold text-[#222]" : "text-[#444]"}`}>
      <input
        type="checkbox"
        className="w-4.5 h-4.5 shrink-0 cursor-pointer"
        style={{ accentColor: "#e16036" }}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {children}
    </label>
  );
}

export default function RefundPolicySection({
  type,
  cancelRuleAgree,
  purchaseOneAgree,
  purchaseAllAgree,
  onCancelRuleChange,
  onPurchaseAgreeChange,
  onAllAgreeChange,
}: RefundPolicySectionProps) {
  const allChecked = cancelRuleAgree && purchaseOneAgree;
  const sectionTitle = type === "package" ? "주문내용 확인 및 결제동의" : "취소 및 환불 규정";

  const handleAllAgree = (checked: boolean) => {
    onCancelRuleChange(checked);
    onPurchaseAgreeChange(checked);
    onAllAgreeChange?.(checked);
  };

  return (
    <div className="bg-white rounded-[14px] shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden">
      <div className="flex items-center h-13.5 px-5 border-b border-[#F2F2F2]">
        <h5 className="font-gmarket m-0 text-base font-semibold">{sectionTitle}</h5>
      </div>
      <div className="p-5">
        <RefundText type={type} />
        {type === "shopping" && <hr className="border-[#F2F2F2] my-3" />}

        <div className="flex flex-col gap-3.5">
          {/* 전체 동의 */}
          <div className="mb-2.5 p-3 bg-gray-100 rounded-xl">
            <AgreementCheckbox checked={allChecked} onChange={handleAllAgree} bold>
              전체 동의하기
            </AgreementCheckbox>
          </div>
          <hr className="border-[#F2F2F2] my-0" />

          <AgreementCheckbox checked={cancelRuleAgree} onChange={onCancelRuleChange}>
            환불 규정에 동의하기 <span className="text-red-500">(필수)</span>
          </AgreementCheckbox>

          {type === "shopping" && onAllAgreeChange && (
            <AgreementCheckbox checked={purchaseAllAgree ?? false} onChange={onAllAgreeChange}>
              전체 배송 조건 확인 
            </AgreementCheckbox>
          )}

          <AgreementCheckbox checked={purchaseOneAgree} onChange={onPurchaseAgreeChange}>
            구매조건 확인 및 결제진행에 동의 <span className="text-red-500">(필수)</span>
          </AgreementCheckbox>
        </div>
      </div>
    </div>
  );
}
